/**
 * Pixel Hit Tester
 *
 * Camera-projection + screen-pixel distance helpers used by interactive
 * tools to hover/select scene objects. Hit-testing in pixel space avoids
 * the float32 precision issues that bite three.js's Raycaster against
 * scaled meshes at UTM-scale world coords (the inverse-world-matrix
 * transform loses precision when the camera is zoomed deep on geometry
 * far from the world origin).
 *
 * Instance methods do per-frame projection (cache the active camera +
 * canvas size on construction-time access). Static helpers are pure
 * 2D math and have no viewer dependency.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';

interface Point2DLike {
  x: number;
  y: number;
}

export class PixelHitTester {
  constructor(private viewer: Viewer) {}

  /**
   * Project a world-space point to canvas-pixel coordinates. Returns null
   * for points behind the camera (NDC z outside [-1, 1]) — for ortho
   * cameras this is meaningless but harmless; for perspective it correctly
   * filters out points "behind the viewer" that would otherwise project to
   * an arbitrary on-screen pixel.
   */
  projectWorldToPixel(p: { x: number; y: number; z: number }): THREE.Vector2 | null {
    const camera = this.viewer.scene.getActiveCamera();
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const v = new THREE.Vector3(p.x, p.y, p.z).project(camera);
    if (v.z < -1 || v.z > 1) return null;
    return new THREE.Vector2((v.x + 1) * 0.5 * rect.width, (-v.y + 1) * 0.5 * rect.height);
  }

  /** Cursor position, expressed as canvas-relative pixels. */
  mousePx(event: MouseEvent): THREE.Vector2 {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /** Shortest distance from `p` to segment `[a, b]`, all in 2D pixel space. */
  static pointToSegmentDistancePx(p: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): number {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const len2 = abx * abx + aby * aby;
    if (len2 < 1e-9) return p.distanceTo(a);
    const t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / len2, 0, 1);
    const cx = a.x + abx * t;
    const cy = a.y + aby * t;
    const dx = p.x - cx;
    const dy = p.y - cy;
    return Math.hypot(dx, dy);
  }

  /**
   * Cohen-Sutherland line-vs-rectangle intersection test in 2D pixel space.
   * Returns true if the segment [a, b] has any overlap with the axis-aligned
   * rectangle [minX, minY, maxX, maxY] — including the case where both
   * endpoints are outside but the segment passes through the box.
   */
  static segmentIntersectsRect(a: THREE.Vector2, b: THREE.Vector2, minX: number, minY: number, maxX: number, maxY: number): boolean {
    // Outcode bits: 1=left, 2=right, 4=below, 8=above.
    const computeOutcode = (x: number, y: number): number => {
      let code = 0;
      if (x < minX) code |= 1;
      else if (x > maxX) code |= 2;
      if (y < minY) code |= 4;
      else if (y > maxY) code |= 8;
      return code;
    };

    let x1 = a.x;
    let y1 = a.y;
    let x2 = b.x;
    let y2 = b.y;
    let code1 = computeOutcode(x1, y1);
    let code2 = computeOutcode(x2, y2);

    // Standard Cohen-Sutherland clipping loop. We don't need the clipped
    // segment back, just a yes/no, but the loop is the cleanest correct path.
    // Bounded iteration count: 4 sides × 2 endpoints ≤ 8 steps in the worst case.
    for (let i = 0; i < 8; i++) {
      if ((code1 | code2) === 0) return true; // both inside, fully accepted
      if ((code1 & code2) !== 0) return false; // both share an outside region — trivial reject
      // Pick the endpoint with a non-zero outcode and clip it to the box.
      const outcode = code1 !== 0 ? code1 : code2;
      let x = 0;
      let y = 0;
      if (outcode & 8) {
        // above
        x = x1 + ((x2 - x1) * (maxY - y1)) / (y2 - y1);
        y = maxY;
      } else if (outcode & 4) {
        // below
        x = x1 + ((x2 - x1) * (minY - y1)) / (y2 - y1);
        y = minY;
      } else if (outcode & 2) {
        // right
        y = y1 + ((y2 - y1) * (maxX - x1)) / (x2 - x1);
        x = maxX;
      } else if (outcode & 1) {
        // left
        y = y1 + ((y2 - y1) * (minX - x1)) / (x2 - x1);
        x = minX;
      }
      if (outcode === code1) {
        x1 = x;
        y1 = y;
        code1 = computeOutcode(x1, y1);
      } else {
        x2 = x;
        y2 = y;
        code2 = computeOutcode(x2, y2);
      }
    }
    return false;
  }

  /** Axis-aligned rect containment, both in pixel space. */
  static pointInRect(p: Point2DLike, minX: number, minY: number, maxX: number, maxY: number): boolean {
    return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
  }

  /**
   * Ray-casting point-in-polygon test, 2D, ignores any Z. Works for both
   * world-space (Vector3-shaped) polygons and pixel-space (Vector2)
   * polygons since it only reads `.x` and `.y`.
   */
  static isPointInPolygon2D(point: Point2DLike, polygon: Point2DLike[]): boolean {
    let inside = false;
    const x = point.x;
    const y = point.y;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }

    return inside;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
